import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { ClipboardList, RefreshCw, Mail, Clock, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('auth_token') || '';

export default function AdminLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      setLeads(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const exportCSV = () => {
    const csv = ['First,Last,Email,Org,Products,Date', ...leads.map(l => `${l.first_name},${l.last_name},${l.email},${l.organization || ''},${(l.selected_products || []).join('|')},${l.created_at || ''}`)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'subscription-leads.csv'; a.click();
  };

  return (
    <AdminLayout title="Inquiries" breadcrumb="Forms / Leads">
      <div style={{ maxWidth: 1000 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: 20, fontWeight: 800, color: '#07121f', letterSpacing: '-.05em', margin: '0 0 4px' }}>
              Subscription Leads
            </h1>
            <p style={{ fontSize: 13, color: 'var(--op-muted)', margin: 0 }}>{leads.length} lead{leads.length !== 1 ? 's' : ''} total</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#fff', border: '1px solid rgba(221,212,197,0.7)', borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--op-muted)', cursor: 'pointer' }}>
              <RefreshCw size={12} /> Refresh
            </button>
            <button onClick={exportCSV} data-testid="export-leads-btn" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#081D30', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              <Download size={12} /> Export CSV
            </button>
          </div>
        </div>

        <div style={{ background: '#fff', border: '1px solid rgba(221,212,197,0.7)', borderRadius: 12, overflow: 'hidden' }} data-testid="leads-table">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px', color: 'var(--op-muted)' }}>Loading…</div>
          ) : leads.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', color: 'var(--op-muted)' }}>
              <ClipboardList size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
              <p>No subscription leads yet</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F8F6F0', borderBottom: '1px solid rgba(221,212,197,0.5)' }}>
                  {['Name', 'Email', 'Organization', 'Products', 'Date'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--op-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((lead, i) => (
                  <tr key={lead.id || i} style={{ borderBottom: '1px solid rgba(221,212,197,0.3)' }} data-testid="lead-row">
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#07121f' }}>{lead.first_name} {lead.last_name}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Mail size={12} color="var(--op-teal)" />
                        <span style={{ fontSize: 12, color: '#07121f' }}>{lead.email}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--op-muted)' }}>{lead.organization || '—'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(lead.selected_products || []).map(p => (
                          <span key={p} style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: 'rgba(0,140,134,.1)', color: '#008C86' }}>{p}</span>
                        ))}
                        {(!lead.selected_products || lead.selected_products.length === 0) && <span style={{ fontSize: 12, color: 'var(--op-muted)' }}>—</span>}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--op-muted)' }}>
                      {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
